import { useState, useEffect } from 'react';
import './UserForm.css';

/**
 * User Form Component
 * For creating and editing users with role assignment
 */

function UserForm({ user, apiBase, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    is_active: true,
    role_codes: [],
  });

  const [availableRoles, setAvailableRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch available roles from backend
  useEffect(() => {
    fetchRoles();
  }, [apiBase]);

  const fetchRoles = async () => {
    try {
      setRolesLoading(true);
      const response = await fetch(`${apiBase}/admin/roles`, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch roles');
      }

      const data = await response.json();
      if (data.ok && Array.isArray(data.data)) {
        // Filter to only show the 3 correct roles
        const correctRoles = ['admin', 'admissions', 'approver'];
        const roles = data.data
          .filter(role => correctRoles.includes(role.role_code || role.code))
          .map(role => ({
            code: role.role_code || role.code,
            label: role.role_name || role.label,
          }))
          .sort((a, b) => {
            // Sort in order: admin, admissions, approver
            const order = ['admin', 'admissions', 'approver'];
            return order.indexOf(a.code) - order.indexOf(b.code);
          });
        setAvailableRoles(roles);
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
      // Fallback roles if API fails
      setAvailableRoles([
        { code: 'admin', label: 'Admin (Website/Content)' },
        { code: 'admissions', label: 'Admin Admissions' },
        { code: 'approver', label: 'Principal (Content Approver)' },
      ]);
    } finally {
      setRolesLoading(false);
    }
  };

  // Load user data if editing
  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name,
        email: user.email,
        password: '',
        is_active: user.is_active,
        role_codes: user.role_codes || [],
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleRoleChange = (e) => {
    const roleCode = e.target.value;
    if (roleCode) {
      setFormData(prev => ({
        ...prev,
        role_codes: [roleCode],
      }));
    }
  };

  const handleToggleActive = () => {
    setFormData(prev => ({
      ...prev,
      is_active: !prev.is_active,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.full_name.trim()) {
      setError('Full name is required');
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }

    if (!user && !formData.password) {
      setError('Password is required for new users');
      return;
    }

    if (formData.password && formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (formData.role_codes.length === 0) {
      setError('A role must be selected');
      return;
    }

    setLoading(true);

    try {
      const method = user ? 'PUT' : 'POST';
      const url = user
        ? `${apiBase}/admin/users/${user.id}`
        : `${apiBase}/admin/users`;

      const payload = { ...formData };
      if (!payload.password) delete payload.password;

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to save user');
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{user ? 'Edit User' : 'Create New User'}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="user-form">
          {error && (
            <div className="form-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="full_name">Full Name *</label>
            <input
              id="full_name"
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              placeholder="Enter full name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email address"
              required
              disabled={!!user}
              title={user ? 'Email cannot be changed' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">
              Password {!user ? '*' : '(leave blank to keep current)'}
            </label>
            <input
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={user ? 'Leave blank to keep current' : 'Enter password'}
              minLength={8}
            />
            <small>Minimum 8 characters</small>
          </div>

          <div className="form-group">
            <label htmlFor="role_select">Role *</label>
            <select
              id="role_select"
              value={formData.role_codes[0] || ''}
              onChange={handleRoleChange}
              required
              className="role-select"
              disabled={rolesLoading}
            >
              <option value="">
                {rolesLoading ? 'Loading roles...' : '-- Select a role --'}
              </option>
              {availableRoles.map(role => (
                <option 
                  key={role.code} 
                  value={role.code}
                >
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Account Status</label>
            <div className="status-row">
              <button
                type="button"
                className={`toggle-switch ${formData.is_active ? 'active' : 'inactive'}`}
                onClick={handleToggleActive}
              >
                <span className="toggle-slider"></span>
              </button>
              <span className="status-text">
                {formData.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (user ? 'Update User' : 'Create User')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserForm;
